import { useState } from 'react';
import { Stack } from '@mui/material';
import {
  ExpandableContentButton,
  ExpandableContentWrapper,
} from 'components/shared';
import { CanvasGroupLinkButton } from 'components/canvas/groups';
import {
  TermStructureChartSettings,
  TermStructureZoomOutButton,
  TermStructureDownloadButton,
  TermStructureProvider,
} from 'components/implied_vol/TermStructure';
import TermStructureChart from 'components/implied_vol/TermStructure/TermStructureChart';
import { CanvasComponentConfig, CanvasContentId } from 'types/canvas';
import { DEFAULT_TERM_STRUCTURE_CONFIG, TermStructureConfig } from 'types';
import { useRecoilValue } from 'recoil';
import { hiroSymbolsState } from 'states/hiro';
import CanvasSymSelector from './CanvasSymSelector';

interface CanvasTermStructureProps {
  id: number;
  sym: string;
  config: CanvasComponentConfig;
  onSymChange: (sym: string) => void;
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void;
}

const CanvasTermStructure = ({
  id,
  sym,
  config: initialConfig,
  onSymChange,
  onConfigChange,
}: CanvasTermStructureProps) => {
  const hiroSymbols = useRecoilValue(hiroSymbolsState);
  const symOptions = Array.from(hiroSymbols)
    .sort()
    .map((s) => ({ symbol: s }));

  const [config, setConfig] = useState<TermStructureConfig>(() => ({
    ...DEFAULT_TERM_STRUCTURE_CONFIG,
    ...(initialConfig as Partial<TermStructureConfig>),
  }));

  const handleConfigChange = (patch: Partial<TermStructureConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
    onConfigChange(patch as Partial<CanvasComponentConfig>);
  };

  const expandableContentButtonType = `${CanvasContentId.TERM_STRUCTURE}-${id}`;

  return (
    <ExpandableContentWrapper
      type={expandableContentButtonType}
      styleOverrides={{ p: 1, height: '100%' }}
    >
      <TermStructureProvider
        selectedSym={sym}
        config={config}
        onConfigChange={handleConfigChange}
      >
        <Stack
          direction="column"
          spacing={1}
          sx={{ height: '100%', width: '100%' }}
        >
          <Stack
            sx={{
              px: 1,
              py: 0.5,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <CanvasSymSelector
              sym={sym}
              options={symOptions}
              onSymChange={onSymChange}
            />
            <TermStructureZoomOutButton />
            <TermStructureDownloadButton />
            <TermStructureChartSettings />
            <CanvasGroupLinkButton
              componentId={id}
              contentId={CanvasContentId.TERM_STRUCTURE}
            />
            <ExpandableContentButton type={expandableContentButtonType} />
          </Stack>
          <TermStructureChart />
        </Stack>
      </TermStructureProvider>
    </ExpandableContentWrapper>
  );
};

export default CanvasTermStructure;
